import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

const uid = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`

const HISTORY_LIMIT = 60

// Subset of state that participates in undo/redo. Anything else (toasts,
// processing flag, viewer, ephemeral UI) is excluded.
const HISTORICAL_KEYS = [
  'documents',
  'pageOrder',
  'selectedPageId',
  'selectedDocumentId',
  'splitGroups',
  'exportOptions',
  'stampSettings',
  'signatures',
  'formTemplates',
]

const defaultExportOptions = {
  scrubMetadata: true,
  bates: {
    enabled: false,
    prefix: '',
    start: 1,
    position: 'bottom-right', // bottom-left | bottom-center | bottom-right | top-left | top-center | top-right
    size: 10,
    color: '#475569',
  },
  watermark: {
    enabled: false,
    text: '',
    color: '#94a3b8',
    size: 64,
    opacity: 0.2,
    angle: 45,
  },
  compression: 'normal', // 'normal' | 'compact' | 'rasterize'
  rasterizeDpi: 110,
  mode: 'merged', // 'merged' | 'split' | 'images'
  imageFormat: 'png', // for images mode: 'png' | 'jpg'
  imageDpi: 144,
}

const initialState = {
  documents: [],
  pageOrder: [],
  selectedPageId: null,
  selectedDocumentId: null,
  isProcessing: false,
  processingMessage: '',
  toolMode: 'select', // select | stamp | redact | crop | highlight | ink | sticky | signature
  stampSettings: {
    text: 'CONFIDENTIAL',
    size: 18,
    color: '#dc2626',
  },
  annotationSettings: {
    highlightColor: '#fde047',
    inkColor: '#0f172a',
    inkSize: 2,
    stickyColor: '#fde68a',
    signatureId: null,
  },
  signatures: [], // [{ id, name, dataUrl, width, height }]
  formTemplates: [], // [{ id, name, values: { fieldName: value, ... } }]
  splitGroups: [], // [{ id, name, pageIds: [...] }]
  exportOptions: defaultExportOptions,
  viewerPageId: null,
  theme: 'system', // light | dark | system
  ocrLanguage: 'eng',
  history: { past: [], future: [] },
  toast: null,
}

// Build a clean per-page record.
export function makeBlankPage(overrides = {}) {
  return {
    id: uid(),
    originalIndex: 0,
    width: 612,
    height: 792,
    rotation: 0,
    included: true,
    stamps: [],
    redactions: [],
    crop: null,
    ocrText: [],
    ocrApplied: false,
    ocrLanguage: 'eng',
    annotations: [], // [{ id, type, ... }]
    sourceKind: 'pdf', // 'pdf' | 'image'
    ...overrides,
  }
}

function snapshot(state) {
  const out = {}
  for (const k of HISTORICAL_KEYS) out[k] = state[k]
  return out
}

function applySnapshot(state, snap) {
  return { ...state, ...snap }
}

function pushHistory(state, patch) {
  const before = snapshot(state)
  const past = state.history.past.concat(before).slice(-HISTORY_LIMIT)
  return {
    ...patch,
    history: { past, future: [] },
  }
}

export const useProjectStore = create(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    setProcessing: (isProcessing, processingMessage = '') =>
      set({ isProcessing, processingMessage }),

    showToast: (message, kind = 'info') => {
      set({ toast: { id: uid(), message, kind } })
      setTimeout(() => {
        const current = get().toast
        if (current && current.message === message) set({ toast: null })
      }, 3500)
    },
    dismissToast: () => set({ toast: null }),

    setTheme: (theme) => set({ theme }),
    setToolMode: (toolMode) => set({ toolMode }),
    setStampSettings: (patch) =>
      set((s) => ({ stampSettings: { ...s.stampSettings, ...patch } })),
    setAnnotationSettings: (patch) =>
      set((s) => ({ annotationSettings: { ...s.annotationSettings, ...patch } })),
    setExportOption: (patch) =>
      set((s) => ({ exportOptions: { ...s.exportOptions, ...patch } })),
    setExportSubOption: (key, patch) =>
      set((s) => ({
        exportOptions: {
          ...s.exportOptions,
          [key]: { ...s.exportOptions[key], ...patch },
        },
      })),
    setOcrLanguage: (lang) => set({ ocrLanguage: lang }),

    openViewer: (pageId) => set({ viewerPageId: pageId }),
    closeViewer: () => set({ viewerPageId: null }),

    // --- Undo / redo --------------------------------------------------------

    undo: () =>
      set((state) => {
        const past = state.history.past
        if (past.length === 0) return {}
        const previous = past[past.length - 1]
        const newPast = past.slice(0, -1)
        const current = snapshot(state)
        return {
          ...applySnapshot(state, previous),
          history: {
            past: newPast,
            future: [current, ...state.history.future].slice(0, HISTORY_LIMIT),
          },
        }
      }),

    redo: () =>
      set((state) => {
        const future = state.history.future
        if (future.length === 0) return {}
        const next = future[0]
        const newFuture = future.slice(1)
        const current = snapshot(state)
        return {
          ...applySnapshot(state, next),
          history: {
            past: state.history.past.concat(current).slice(-HISTORY_LIMIT),
            future: newFuture,
          },
        }
      }),

    // --- Documents ----------------------------------------------------------

    addDocument: (doc) =>
      set((state) =>
        pushHistory(state, {
          documents: [...state.documents, doc],
          pageOrder: [...state.pageOrder, ...doc.pages.map((p) => p.id)],
          selectedDocumentId: state.selectedDocumentId || doc.id,
        })
      ),

    updateDocument: (id, patch) =>
      set((state) =>
        pushHistory(state, {
          documents: state.documents.map((d) =>
            d.id === id ? { ...d, ...patch } : d
          ),
        })
      ),

    removeDocument: (id) =>
      set((state) => {
        const docToRemove = state.documents.find((d) => d.id === id)
        const removedPageIds = new Set((docToRemove?.pages || []).map((p) => p.id))
        const documents = state.documents.filter((d) => d.id !== id)
        return pushHistory(state, {
          documents,
          pageOrder: state.pageOrder.filter((pid) => !removedPageIds.has(pid)),
          selectedPageId: removedPageIds.has(state.selectedPageId)
            ? null
            : state.selectedPageId,
          selectedDocumentId:
            state.selectedDocumentId === id
              ? documents[0]?.id || null
              : state.selectedDocumentId,
          splitGroups: state.splitGroups.map((g) => ({
            ...g,
            pageIds: g.pageIds.filter((id) => !removedPageIds.has(id)),
          })),
        })
      }),

    clearAll: () => set({ ...initialState, theme: get().theme }),

    togglePageIncluded: (pageId) =>
      set((state) => mapPageH(state, pageId, (p) => ({ ...p, included: !p.included }))),
    setPageIncluded: (pageId, included) =>
      set((state) => mapPageH(state, pageId, (p) => ({ ...p, included }))),
    setAllPagesIncluded: (included) =>
      set((state) =>
        pushHistory(state, {
          documents: state.documents.map((d) => ({
            ...d,
            pages: d.pages.map((p) => ({ ...p, included })),
          })),
        })
      ),
    invertSelection: () =>
      set((state) =>
        pushHistory(state, {
          documents: state.documents.map((d) => ({
            ...d,
            pages: d.pages.map((p) => ({ ...p, included: !p.included })),
          })),
        })
      ),

    reorderPages: (newOrder) =>
      set((state) => pushHistory(state, { pageOrder: newOrder })),

    duplicatePage: (pageId) =>
      set((state) => {
        let newId = null
        const documents = state.documents.map((d) => {
          const idx = d.pages.findIndex((p) => p.id === pageId)
          if (idx === -1) return d
          const original = d.pages[idx]
          const dup = { ...JSON.parse(JSON.stringify(original)), id: uid() }
          newId = dup.id
          const pages = [...d.pages]
          pages.splice(idx + 1, 0, dup)
          return { ...d, pages }
        })
        if (!newId) return {}
        const orderIdx = state.pageOrder.indexOf(pageId)
        const pageOrder = [...state.pageOrder]
        pageOrder.splice(orderIdx + 1, 0, newId)
        return pushHistory(state, { documents, pageOrder })
      }),

    splitPage: (pageId, axis = 'horizontal') =>
      set((state) => {
        let newId = null
        const documents = state.documents.map((d) => {
          const idx = d.pages.findIndex((p) => p.id === pageId)
          if (idx === -1) return d
          const original = d.pages[idx]
          // Top/Left half stays at the original page (with crop), bottom/right
          // is the duplicate (with complementary crop).
          const halfA = {
            ...original,
            crop:
              axis === 'horizontal'
                ? { xRatio: 0, yRatio: 0, wRatio: 1, hRatio: 0.5 }
                : { xRatio: 0, yRatio: 0, wRatio: 0.5, hRatio: 1 },
          }
          const dup = {
            ...JSON.parse(JSON.stringify(original)),
            id: uid(),
            crop:
              axis === 'horizontal'
                ? { xRatio: 0, yRatio: 0.5, wRatio: 1, hRatio: 0.5 }
                : { xRatio: 0.5, yRatio: 0, wRatio: 0.5, hRatio: 1 },
          }
          newId = dup.id
          const pages = [...d.pages]
          pages[idx] = halfA
          pages.splice(idx + 1, 0, dup)
          return { ...d, pages }
        })
        if (!newId) return {}
        const orderIdx = state.pageOrder.indexOf(pageId)
        const pageOrder = [...state.pageOrder]
        pageOrder.splice(orderIdx + 1, 0, newId)
        return pushHistory(state, { documents, pageOrder })
      }),

    removePage: (pageId) =>
      set((state) =>
        pushHistory(state, {
          documents: state.documents.map((d) => ({
            ...d,
            pages: d.pages.filter((p) => p.id !== pageId),
          })),
          pageOrder: state.pageOrder.filter((id) => id !== pageId),
          selectedPageId: state.selectedPageId === pageId ? null : state.selectedPageId,
          splitGroups: state.splitGroups.map((g) => ({
            ...g,
            pageIds: g.pageIds.filter((id) => id !== pageId),
          })),
        })
      ),

    selectPage: (pageId) =>
      set((state) => {
        const owningDoc = state.documents.find((d) =>
          d.pages.some((p) => p.id === pageId)
        )
        return {
          selectedPageId: pageId,
          selectedDocumentId: owningDoc?.id || state.selectedDocumentId,
        }
      }),
    selectDocument: (id) => set({ selectedDocumentId: id, selectedPageId: null }),

    // --- AcroForm -----------------------------------------------------------

    setFormFields: (docId, fields) =>
      set((state) =>
        pushHistory(state, {
          documents: state.documents.map((d) =>
            d.id === docId ? { ...d, formFields: fields, formScanned: true } : d
          ),
        })
      ),
    updateFormFieldValue: (docId, fieldName, value) =>
      set((state) =>
        pushHistory(state, {
          documents: state.documents.map((d) => {
            if (d.id !== docId) return d
            const formFields = (d.formFields || []).map((f) =>
              f.name === fieldName ? { ...f, value } : f
            )
            return { ...d, formFields }
          }),
        })
      ),

    saveFormTemplate: (name) =>
      set((state) => {
        if (!state.selectedDocumentId) return {}
        const doc = state.documents.find((d) => d.id === state.selectedDocumentId)
        if (!doc || !doc.formFields) return {}
        const values = {}
        for (const f of doc.formFields) values[f.name] = f.value
        return pushHistory(state, {
          formTemplates: [
            ...state.formTemplates,
            { id: uid(), name: name || `Template ${state.formTemplates.length + 1}`, values },
          ],
        })
      }),
    applyFormTemplate: (templateId) =>
      set((state) => {
        if (!state.selectedDocumentId) return {}
        const tpl = state.formTemplates.find((t) => t.id === templateId)
        if (!tpl) return {}
        const documents = state.documents.map((d) => {
          if (d.id !== state.selectedDocumentId) return d
          if (!d.formFields) return d
          const formFields = d.formFields.map((f) =>
            tpl.values[f.name] !== undefined ? { ...f, value: tpl.values[f.name] } : f
          )
          return { ...d, formFields }
        })
        return pushHistory(state, { documents })
      }),
    deleteFormTemplate: (templateId) =>
      set((state) =>
        pushHistory(state, {
          formTemplates: state.formTemplates.filter((t) => t.id !== templateId),
        })
      ),

    // --- Stamps -------------------------------------------------------------
    addStampToPage: (pageId, stamp) =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({
          ...p,
          stamps: [...(p.stamps || []), { ...stamp, id: uid() }],
        }))
      ),
    removeStamp: (pageId, stampId) =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({
          ...p,
          stamps: (p.stamps || []).filter((s) => s.id !== stampId),
        }))
      ),
    clearStampsOnPage: (pageId) =>
      set((state) => mapPageH(state, pageId, (p) => ({ ...p, stamps: [] }))),

    // --- Redactions ---------------------------------------------------------
    addRedaction: (pageId, rect) =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({
          ...p,
          redactions: [...(p.redactions || []), { ...rect, id: uid() }],
        }))
      ),
    removeRedaction: (pageId, redactionId) =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({
          ...p,
          redactions: (p.redactions || []).filter((r) => r.id !== redactionId),
        }))
      ),
    clearRedactionsOnPage: (pageId) =>
      set((state) => mapPageH(state, pageId, (p) => ({ ...p, redactions: [] }))),

    // --- Crop / Rotation ----------------------------------------------------
    setCrop: (pageId, rect) =>
      set((state) => mapPageH(state, pageId, (p) => ({ ...p, crop: rect }))),
    clearCrop: (pageId) =>
      set((state) => mapPageH(state, pageId, (p) => ({ ...p, crop: null }))),

    rotatePage: (pageId, deltaDegrees = 90) =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({
          ...p,
          rotation: (((p.rotation || 0) + deltaDegrees) % 360 + 360) % 360,
        }))
      ),
    setRotation: (pageId, degrees) =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({
          ...p,
          rotation: ((degrees % 360) + 360) % 360,
        }))
      ),
    resetRotation: (pageId) =>
      set((state) => mapPageH(state, pageId, (p) => ({ ...p, rotation: 0 }))),

    // --- OCR ----------------------------------------------------------------
    setOcrText: (pageId, items, language = 'eng') =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({
          ...p,
          ocrText: items,
          ocrApplied: false,
          ocrLanguage: language,
        }))
      ),
    applyOcr: (pageId, applied = true) =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({ ...p, ocrApplied: applied }))
      ),
    clearOcr: (pageId) =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({
          ...p,
          ocrText: [],
          ocrApplied: false,
        }))
      ),

    // --- Annotations (highlight / ink / sticky / signature) -----------------
    addAnnotation: (pageId, annotation) =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({
          ...p,
          annotations: [...(p.annotations || []), { ...annotation, id: uid() }],
        }))
      ),
    updateAnnotation: (pageId, annotationId, patch) =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({
          ...p,
          annotations: (p.annotations || []).map((a) =>
            a.id === annotationId ? { ...a, ...patch } : a
          ),
        }))
      ),
    removeAnnotation: (pageId, annotationId) =>
      set((state) =>
        mapPageH(state, pageId, (p) => ({
          ...p,
          annotations: (p.annotations || []).filter((a) => a.id !== annotationId),
        }))
      ),
    clearAnnotationsOnPage: (pageId) =>
      set((state) => mapPageH(state, pageId, (p) => ({ ...p, annotations: [] }))),

    // --- Signatures library -------------------------------------------------
    addSignature: (sig) =>
      set((state) =>
        pushHistory(state, {
          signatures: [...state.signatures, { ...sig, id: uid() }],
        })
      ),
    removeSignature: (sigId) =>
      set((state) =>
        pushHistory(state, {
          signatures: state.signatures.filter((s) => s.id !== sigId),
        })
      ),

    // --- Split groups -------------------------------------------------------
    addSplitGroup: (name) =>
      set((state) =>
        pushHistory(state, {
          splitGroups: [
            ...state.splitGroups,
            { id: uid(), name: name || `Group ${state.splitGroups.length + 1}`, pageIds: [] },
          ],
        })
      ),
    renameSplitGroup: (groupId, name) =>
      set((state) =>
        pushHistory(state, {
          splitGroups: state.splitGroups.map((g) =>
            g.id === groupId ? { ...g, name } : g
          ),
        })
      ),
    removeSplitGroup: (groupId) =>
      set((state) =>
        pushHistory(state, {
          splitGroups: state.splitGroups.filter((g) => g.id !== groupId),
        })
      ),
    togglePageInSplitGroup: (groupId, pageId) =>
      set((state) =>
        pushHistory(state, {
          splitGroups: state.splitGroups.map((g) => {
            if (g.id !== groupId) return g
            const has = g.pageIds.includes(pageId)
            return {
              ...g,
              pageIds: has
                ? g.pageIds.filter((id) => id !== pageId)
                : [...g.pageIds, pageId],
            }
          }),
        })
      ),

    // --- Wholesale state restore (used by IndexedDB rehydrate) -------------
    hydrateFrom: (saved) =>
      set((state) => ({
        ...state,
        ...saved,
        // never restore ephemeral fields
        isProcessing: false,
        processingMessage: '',
        toast: null,
        viewerPageId: null,
        history: { past: [], future: [] },
      })),
  }))
)

function mapPageH(state, pageId, transform) {
  return pushHistory(state, {
    documents: state.documents.map((d) => ({
      ...d,
      pages: d.pages.map((p) => (p.id === pageId ? transform(p) : p)),
    })),
  })
}

// Pure derivation helpers -----------------------------------------------------

export function deriveOrderedPages(documents, pageOrder) {
  const map = new Map()
  for (const d of documents) {
    for (const p of d.pages) {
      map.set(p.id, { page: p, documentId: d.id, documentName: d.originalName })
    }
  }
  const out = []
  for (const id of pageOrder) {
    const entry = map.get(id)
    if (entry) {
      out.push({
        ...entry.page,
        documentId: entry.documentId,
        documentName: entry.documentName,
      })
    }
  }
  return out
}

export function findPageById(documents, pageId) {
  if (!pageId) return null
  for (const d of documents) {
    const p = d.pages.find((x) => x.id === pageId)
    if (p) return p
  }
  return null
}

export function findDocumentById(documents, docId) {
  if (!docId) return null
  return documents.find((d) => d.id === docId) || null
}

export { uid }
